import { useMemo, useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Divider,
  Link,
  Tooltip,
  Radio,
  Box,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import { phoneFormatter } from '../../../../utils/redux-form-helper';
import messages from './messages';
import PortalBadge from './PortalBadge';

// Reusable Components
const ProspectTitle = ({ title, prospectId }) => {
  const currentUrl = window.location.pathname;
  const baseUrl = currentUrl.split('?')[0];
  const prospectUrl = `${baseUrl}prospect/${prospectId}`;

  return {
    variant: 'custom',
    component: (
      <Link
        href={prospectUrl}
        target="_blank"
        underline="hover"
        rel="noopener noreferrer"
        sx={{
          color: 'primary.main',
          cursor: 'pointer',
        }}
      >
        <Typography variant="titleMedium">{title}</Typography>
      </Link>
    ),
    GridProps: { xs: 6, paddingTop: 4 },
  };
};

const SectionTitle = ({ title }) => ({
  variant: 'custom',
  component: <Typography variant="labelMedium">{title}</Typography>,
  GridProps: { xs: 6 },
});

const CheckboxField = ({
  name,
  label,
  disabled = false,
  defaultChecked = false,
}) => ({
  name,
  variant: 'checkbox',
  label,
  disabled,
  defaultChecked,
  GridProps: { xs: 6 },
});

const ExclusiveCheckboxField = ({
  name,
  label,
  otherFieldName,
  formKey,
  watch,
  setValue,
  otherLabel,
}) => {
  const areIdentical = label === otherLabel;

  // Use shared field name for identical labels (auto-sync!)
  let fieldName = name;
  if (areIdentical) {
    const sanitizedLabel = label.replace(/[^a-zA-Z0-9]/g, '_');
    fieldName = `${formKey}_shared_name_${sanitizedLabel}`;
  }

  const isOtherChecked = watch ? watch(otherFieldName) : false;

  return {
    name: fieldName,
    variant: 'checkbox',
    label,
    disabled: !areIdentical && isOtherChecked,
    onChange: (event) => {
      const checked = event.target.checked;

      if (setValue) {
        if (checked) {
          setValue(fieldName, true);
          if (!areIdentical) {
            setValue(otherFieldName, false);
          }
        } else {
          setValue(fieldName, false);
        }
      }
    },
    GridProps: { xs: 6 },
  };
};

const ContactCheckboxField = ({
  name,
  contact,
  isPhone = false,
  uniqueCount = 0,
  isDuplicated = false,
  isPrimary = false,
  watch,
  setValue,
  formKey,
  primaryProfile,
  duplicateProfile,
  hasPortal = false,
  intl,
}) => {
  // Use shared field name for auto-sync
  let fieldName = name;
  if (isDuplicated) {
    const value = isPhone
      ? contact.phoneNumber
      : contact.emailAddress.toLowerCase();
    const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '_');
    fieldName = `${formKey}_shared_${
      isPhone ? 'phone' : 'email'
    }_${sanitizedValue}`;
  }

  // Calculate current selection count for the same type (email/phone)
  const getCurrentSelectionCount = () => {
    let count = 0;

    if (isPhone) {
      // Count selected phones
      primaryProfile.phones?.forEach((_, index) => {
        if (watch && watch(`${formKey}_primaryPhone${index}`)) count++;
      });
      duplicateProfile.phones?.forEach((_, index) => {
        if (watch && watch(`${formKey}_duplicatePhone${index}`)) count++;
      });
    } else {
      // Count selected emails
      primaryProfile.emails?.forEach((_, index) => {
        if (watch && watch(`${formKey}_primaryEmail${index}`)) count++;
      });
      duplicateProfile.emails?.forEach((_, index) => {
        if (watch && watch(`${formKey}_duplicateEmail${index}`)) count++;
      });
    }

    return count;
  };

  const isCurrentlySelected = watch ? watch(fieldName) : false;
  const currentSelectionCount = watch ? getCurrentSelectionCount() : 0;
  const shouldDisable = !isCurrentlySelected && currentSelectionCount >= 3;

  const displayValue = isPhone
    ? phoneFormatter(contact.phoneNumber)
    : contact.emailAddress;

  return {
    name: fieldName,
    variant: 'checkbox',
    label: (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ maxWidth: '100%', width: '100%' }}
      >
        {isPhone ? (
          <Typography variant="bodyMedium">{displayValue}</Typography>
        ) : (
          <Tooltip title={displayValue} placement="top">
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
            >
              <Typography
                variant="bodyMedium"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayValue}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {!isPhone && hasPortal && <PortalBadge intl={intl} />}
      </Stack>
    ),
    disabled: shouldDisable,
    GridProps: { xs: 6 },
  };
};

const OccupantDisplayField = ({
  occupant,
  isDuplicated = false,
  isPrimary = false,
  intl,
}) => ({
  variant: 'custom',
  component: (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="bodyMedium">
        {`${occupant.firstName} ${occupant.lastName}`.trim()}
        {occupant.relationship?.name && ` (${occupant.relationship.name})`}
      </Typography>
      {occupant.hasPortalAccount && <PortalBadge intl={intl} />}
    </Stack>
  ),
  GridProps: { xs: 6 },
});

const EmptyCell = () => ({
  variant: 'custom',
  component: <div></div>,
  GridProps: { xs: 6 },
});

const EmptyHouseholdCell = () => ({
  variant: 'custom',
  component: (
    <Typography variant="bodyMedium" color="text.disabled">
      ---
    </Typography>
  ),
  GridProps: { xs: 6 },
});

const SectionDivider = () => ({
  variant: 'custom',
  component: <Divider />,
  GridProps: { xs: 12 },
});

const ProfileRadioField = ({
  prospectId,
  selectedProfileId,
  onSelect,
  intl,
  radioGroupName = 'profileSelection',
}) => {
  return {
    variant: 'custom',
    component: (
      <Tooltip
        title={intl.formatMessage(messages.selectProfileTooltip)}
        placement="top"
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => onSelect(prospectId)}
        >
          <Radio
            name={radioGroupName}
            value={prospectId}
            checked={selectedProfileId === prospectId}
            onChange={(event) => onSelect(event.target.value)}
            size="small"
          />
          <Typography variant="bodySmall" color="text.secondary">
            {intl.formatMessage(messages.selectPrimaryProfile)}
          </Typography>
        </Stack>
      </Tooltip>
    ),
    GridProps: { xs: 6 },
  };
};

type Props = {
  currentDuplicateData: any,
  primaryProfile: any,
  duplicateProfile: any,
  intl: Object,
  formKey: string,
  onFormReady?: (formMethods: any) => void,
  selectedProfileId: ?string,
  onProfileSelect: (prospectId: string) => void,
  portalUsers: Array<{ email: string, prospectId: string }>,
};

const DuplicatesForm = ({
  currentDuplicateData,
  primaryProfile,
  duplicateProfile,
  intl,
  formKey,
  onFormReady,
  selectedProfileId,
  onProfileSelect,
  portalUsers,
}: Props) => {
  // Helper function to check if an email has a portal account for a specific prospect
  const hasPortalAccount = (emailAddress, prospectId) => {
    return portalUsers.some(
      (user) => user.email === emailAddress && user.prospectId === prospectId,
    );
  };
  // Helper functions to get unique values and check duplicates
  const getUniqueEmails = useMemo(() => {
    if (!primaryProfile || !duplicateProfile) return [];
    const allEmails = [
      ...primaryProfile.emails.map((e) => e.emailAddress),
      ...duplicateProfile.emails.map((e) => e.emailAddress),
    ];
    return [...new Set(allEmails)];
  }, [primaryProfile, duplicateProfile]);

  const getUniquePhones = useMemo(() => {
    if (!primaryProfile || !duplicateProfile) return [];
    const allPhones = [
      ...primaryProfile.phones.map((p) => p.phoneNumber),
      ...duplicateProfile.phones.map((p) => p.phoneNumber),
    ];
    return [...new Set(allPhones)];
  }, [primaryProfile, duplicateProfile]);

  // Check if primary email exists in duplicate profile
  const isEmailDuplicated = (emailAddress) => {
    const duplicateEmails = duplicateProfile.emails.map((e) =>
      e.emailAddress.toLowerCase(),
    );
    return duplicateEmails.includes(emailAddress.toLowerCase());
  };

  // Check if duplicate email exists in primary profile
  const isDuplicateEmailInPrimary = (emailAddress) => {
    const primaryEmails = primaryProfile.emails.map((e) =>
      e.emailAddress.toLowerCase(),
    );
    return primaryEmails.includes(emailAddress.toLowerCase());
  };

  // Check if primary phone exists in duplicate profile
  const isPhoneDuplicated = (phoneNumber) => {
    const duplicatePhones = duplicateProfile.phones.map((p) => p.phoneNumber);
    return duplicatePhones.includes(phoneNumber);
  };

  // Check if duplicate phone exists in primary profile
  const isDuplicatePhoneInPrimary = (phoneNumber) => {
    const primaryPhones = primaryProfile.phones.map((p) => p.phoneNumber);
    return primaryPhones.includes(phoneNumber);
  };

  const isOccupantDuplicated = (occupantName) => {
    const duplicateOccupants = (
      duplicateProfile?.additionalOccupants || []
    ).map((o) => `${o.firstName} ${o.lastName}`.trim());
    return duplicateOccupants.includes(occupantName);
  };

  // Calculate default values - select only identical/matching items
  const defaultValues = useMemo(() => {
    const values = {};

    if (primaryProfile && duplicateProfile) {
      // Auto-select names if identical
      if (primaryProfile.name === duplicateProfile.name) {
        const sanitizedName = primaryProfile.name.replace(/[^a-zA-Z0-9]/g, '_');
        values[`${formKey}_shared_name_${sanitizedName}`] = true;
      }

      // Auto-select preferred name if identical
      if (
        primaryProfile.preferredName &&
        duplicateProfile.preferredName &&
        primaryProfile.preferredName === duplicateProfile.preferredName
      ) {
        const sanitizedPrefName = primaryProfile.preferredName.replace(
          /[^a-zA-Z0-9]/g,
          '_',
        );
        values[`${formKey}_shared_name_${sanitizedPrefName}`] = true;
      }

      // Auto-select only duplicated (identical) phones
      const processedPhones = new Set();
      primaryProfile.phones?.forEach((phone) => {
        const phoneNumber = phone.phoneNumber;
        if (
          !processedPhones.has(phoneNumber) &&
          duplicateProfile.phones?.some((p) => p.phoneNumber === phoneNumber)
        ) {
          const sanitizedPhone = phoneNumber.replace(/[^a-zA-Z0-9]/g, '_');
          values[`${formKey}_shared_phone_${sanitizedPhone}`] = true;
          processedPhones.add(phoneNumber);
        }
      });

      // Auto-select only duplicated (identical) emails
      const processedEmails = new Set();
      primaryProfile.emails?.forEach((email) => {
        const emailAddress = email.emailAddress.toLowerCase();
        if (
          !processedEmails.has(emailAddress) &&
          duplicateProfile.emails?.some(
            (e) => e.emailAddress.toLowerCase() === emailAddress,
          )
        ) {
          const sanitizedEmail = emailAddress.replace(/[^a-zA-Z0-9]/g, '_');
          values[`${formKey}_shared_email_${sanitizedEmail}`] = true;
          processedEmails.add(emailAddress);
        }
      });
    }

    return values;
  }, [primaryProfile, duplicateProfile, formKey]);

  const reactHookFormProps = useReactHookFormProps({
    shouldUnregister: true,
    defaultValues,
  });

  const { watch, setValue, reset } = reactHookFormProps;
  const [rerenderKey, setRerenderKey] = useState(0);

  // Force re-render when form data changes (for pagination)
  useEffect(() => {
    setRerenderKey((prev) => prev + 1);
  }, [formKey, primaryProfile, duplicateProfile]);

  // Reset form with new default values when pagination changes
  useEffect(() => {
    if (reset && defaultValues) {
      reset(defaultValues);
    }
  }, [reset, defaultValues, formKey]);

  // Call onFormReady when form methods are available
  useEffect(() => {
    if (onFormReady && reactHookFormProps) {
      onFormReady(reactHookFormProps);
    }
  }, [onFormReady, reactHookFormProps]);

  if (!primaryProfile || !duplicateProfile) {
    return null;
  }

  return (
    <Form
      key={`${formKey}-${rerenderKey}`}
      spacing={1}
      ReactHookFormProps={reactHookFormProps}
      fields={[
        ProspectTitle({
          title: intl.formatMessage(messages.primaryProfile),
          prospectId: currentDuplicateData?.mainProspectId,
        }),
        ProspectTitle({
          title: intl.formatMessage(messages.possibleDuplicate),
          prospectId: currentDuplicateData?.duplicateProspectId,
        }),
        ProfileRadioField({
          prospectId: currentDuplicateData?.mainProspectId,
          selectedProfileId,
          onSelect: onProfileSelect,
          intl,
          radioGroupName: formKey,
        }),
        ProfileRadioField({
          prospectId: currentDuplicateData?.duplicateProspectId,
          selectedProfileId,
          onSelect: onProfileSelect,
          intl,
          radioGroupName: formKey,
        }),
        // Name section
        SectionTitle({ title: intl.formatMessage(messages.name) }),
        SectionTitle({ title: intl.formatMessage(messages.name) }),
        ExclusiveCheckboxField({
          name: `${formKey}_primaryName`,
          label: primaryProfile.name,
          otherFieldName: `${formKey}_duplicateName`,
          otherLabel: duplicateProfile.name,
          formKey,
          watch,
          setValue,
        }),
        ExclusiveCheckboxField({
          name: `${formKey}_duplicateName`,
          label: duplicateProfile.name,
          otherFieldName: `${formKey}_primaryName`,
          otherLabel: primaryProfile.name,
          formKey,
          watch,
          setValue,
        }),
        SectionDivider(),

        // Preferred Name section (only if at least one has preferred name)
        ...(() => {
          const hasPrimaryPreferredName = !!primaryProfile.preferredName;
          const hasDuplicatePreferredName = !!duplicateProfile.preferredName;

          if (!hasPrimaryPreferredName && !hasDuplicatePreferredName) {
            // No preferred names at all - don't render section
            return [];
          }

          if (hasPrimaryPreferredName && !hasDuplicatePreferredName) {
            // Only primary has preferred name - auto-select and disable
            return [
              SectionTitle({
                title: intl.formatMessage(messages.preferredName),
              }),
              SectionTitle({
                title: intl.formatMessage(messages.preferredName),
              }),
              CheckboxField({
                name: `${formKey}_primaryPreferredName`,
                label: primaryProfile.preferredName,
                disabled: true,
                defaultChecked: true,
              }),
              EmptyCell(),
              SectionDivider(),
            ];
          }

          if (!hasPrimaryPreferredName && hasDuplicatePreferredName) {
            // Only duplicate has preferred name - auto-select and disable
            return [
              SectionTitle({
                title: intl.formatMessage(messages.preferredName),
              }),
              SectionTitle({
                title: intl.formatMessage(messages.preferredName),
              }),
              EmptyCell(),
              CheckboxField({
                name: `${formKey}_duplicatePreferredName`,
                label: duplicateProfile.preferredName,
                disabled: true,
                defaultChecked: true,
              }),
              SectionDivider(),
            ];
          }

          // Both have preferred names - use exclusive selection
          return [
            SectionTitle({
              title: intl.formatMessage(messages.preferredName),
            }),
            SectionTitle({
              title: intl.formatMessage(messages.preferredName),
            }),
            ExclusiveCheckboxField({
              name: `${formKey}_primaryPreferredName`,
              label: primaryProfile.preferredName,
              otherFieldName: `${formKey}_duplicatePreferredName`,
              otherLabel: duplicateProfile.preferredName,
              formKey,
              watch,
              setValue,
            }),
            ExclusiveCheckboxField({
              name: `${formKey}_duplicatePreferredName`,
              label: duplicateProfile.preferredName,
              otherFieldName: `${formKey}_primaryPreferredName`,
              otherLabel: primaryProfile.preferredName,
              formKey,
              watch,
              setValue,
            }),
            SectionDivider(),
          ];
        })(),

        // Phone section (only if at least one has phones)
        ...(primaryProfile.phones?.length > 0 ||
        duplicateProfile.phones?.length > 0
          ? (() => {
              const uniquePhonesCount = getUniquePhones.length;

              return [
                SectionTitle({ title: intl.formatMessage(messages.phone) }),
                SectionTitle({ title: intl.formatMessage(messages.phone) }),
                // Render phones side by side
                ...Array(
                  Math.max(
                    primaryProfile.phones.length,
                    duplicateProfile.phones.length,
                  ),
                )
                  .fill(null)
                  .map((_, index) => [
                    // Primary phone or empty cell
                    primaryProfile.phones[index]
                      ? ContactCheckboxField({
                          name: `${formKey}_primaryPhone${index}`,
                          contact: primaryProfile.phones[index],
                          isPhone: true,
                          uniqueCount: uniquePhonesCount,
                          isDuplicated: isPhoneDuplicated(
                            primaryProfile.phones[index].phoneNumber,
                          ),
                          isPrimary: true,
                          watch,
                          setValue,
                          formKey,
                          primaryProfile,
                          duplicateProfile,
                          hasPortal: false,
                          intl,
                        })
                      : EmptyCell(),
                    // Duplicate phone or empty cell
                    duplicateProfile.phones[index]
                      ? ContactCheckboxField({
                          name: `${formKey}_duplicatePhone${index}`,
                          contact: duplicateProfile.phones[index],
                          isPhone: true,
                          uniqueCount: uniquePhonesCount,
                          isDuplicated: isDuplicatePhoneInPrimary(
                            duplicateProfile.phones[index].phoneNumber,
                          ),
                          isPrimary: false,
                          watch,
                          setValue,
                          formKey,
                          primaryProfile,
                          duplicateProfile,
                          hasPortal: false,
                          intl,
                        })
                      : EmptyCell(),
                  ])
                  .flat(),
                SectionDivider(),
              ];
            })()
          : []),

        // Email section (only if at least one has emails)
        ...(primaryProfile.emails?.length > 0 ||
        duplicateProfile.emails?.length > 0
          ? (() => {
              const uniqueEmailsCount = getUniqueEmails.length;

              return [
                SectionTitle({ title: intl.formatMessage(messages.email) }),
                SectionTitle({ title: intl.formatMessage(messages.email) }),
                // Render emails side by side
                ...Array(
                  Math.max(
                    primaryProfile.emails.length,
                    duplicateProfile.emails.length,
                  ),
                )
                  .fill(null)
                  .map((_, index) => [
                    // Primary email or empty cell
                    primaryProfile.emails[index]
                      ? ContactCheckboxField({
                          name: `${formKey}_primaryEmail${index}`,
                          contact: primaryProfile.emails[index],
                          isPhone: false,
                          uniqueCount: uniqueEmailsCount,
                          isDuplicated: isEmailDuplicated(
                            primaryProfile.emails[index].emailAddress,
                          ),
                          isPrimary: true,
                          watch,
                          setValue,
                          formKey,
                          primaryProfile,
                          duplicateProfile,
                          hasPortal: hasPortalAccount(
                            primaryProfile.emails[index].emailAddress,
                            currentDuplicateData?.mainProspectId,
                          ),
                          intl,
                        })
                      : EmptyCell(),
                    // Duplicate email or empty cell
                    duplicateProfile.emails[index]
                      ? ContactCheckboxField({
                          name: `${formKey}_duplicateEmail${index}`,
                          contact: duplicateProfile.emails[index],
                          isPhone: false,
                          uniqueCount: uniqueEmailsCount,
                          isDuplicated: isDuplicateEmailInPrimary(
                            duplicateProfile.emails[index].emailAddress,
                          ),
                          isPrimary: false,
                          watch,
                          setValue,
                          formKey,
                          primaryProfile,
                          duplicateProfile,
                          hasPortal: hasPortalAccount(
                            duplicateProfile.emails[index].emailAddress,
                            currentDuplicateData?.duplicateProspectId,
                          ),
                          intl,
                        })
                      : EmptyCell(),
                  ])
                  .flat(),
                SectionDivider(),
              ];
            })()
          : []),

        // Additional Occupants section (only if at least one has occupants)
        ...(primaryProfile?.additionalOccupants?.length > 0 ||
        duplicateProfile?.additionalOccupants?.length > 0
          ? (() => {
              const primaryOccupants =
                primaryProfile?.additionalOccupants || [];
              const duplicateOccupants =
                duplicateProfile?.additionalOccupants || [];

              return [
                SectionTitle({ title: intl.formatMessage(messages.household) }),
                SectionTitle({ title: intl.formatMessage(messages.household) }),
                // Render occupants side by side
                ...(primaryOccupants.length === 0 &&
                duplicateOccupants.length === 0
                  ? [EmptyHouseholdCell(), EmptyHouseholdCell()]
                  : Array(
                      Math.max(
                        primaryOccupants.length || 1,
                        duplicateOccupants.length || 1,
                      ),
                    )
                      .fill(null)
                      .map((_, index) => [
                        // Primary occupant or placeholder
                        primaryOccupants.length === 0 && index === 0
                          ? EmptyHouseholdCell()
                          : primaryOccupants[index]
                          ? OccupantDisplayField({
                              occupant: primaryOccupants[index],
                              isDuplicated: isOccupantDuplicated(
                                `${primaryOccupants[index].firstName} ${primaryOccupants[index].lastName}`.trim(),
                              ),
                              isPrimary: true,
                              intl,
                            })
                          : EmptyCell(),
                        // Duplicate occupant or placeholder
                        duplicateOccupants.length === 0 && index === 0
                          ? EmptyHouseholdCell()
                          : duplicateOccupants[index]
                          ? OccupantDisplayField({
                              occupant: duplicateOccupants[index],
                              isDuplicated: false,
                              isPrimary: false,
                              intl,
                            })
                          : EmptyCell(),
                      ])
                      .flat()),
                SectionDivider(),
              ];
            })()
          : []),

        // Household section - keeping for future implementation
        ...(primaryProfile.household?.length > 0 ||
        duplicateProfile.household?.length > 0
          ? [
              SectionTitle({
                title: intl.formatMessage(messages.household),
              }),
              SectionTitle({
                title: intl.formatMessage(messages.household),
              }),
              ...(primaryProfile.household[0]?.name
                ? [
                    CheckboxField({
                      name: `${formKey}_primaryHousehold1`,
                      label: primaryProfile.household[0].name,
                    }),
                  ]
                : [EmptyCell()]),
              ...(duplicateProfile.household[0]?.name
                ? [
                    CheckboxField({
                      name: `${formKey}_duplicateHousehold1`,
                      label: duplicateProfile.household[0].name,
                    }),
                  ]
                : [EmptyCell()]),
            ]
          : []),
      ]}
    />
  );
};

export default DuplicatesForm;
